// src/pages/OpsExceptions.jsx
import { useEffect, useMemo, useState } from "react";
import AppShell from "../layout/AppShell";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { ENV, tbGetJson, tbPostJson } from "../api/client";
import "./OpsExceptions.css";
import OpsExceptionOverlay from "./OpsExceptionOverlay";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ===== helpers ===== */
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = (() => {
    const val = ENV.PAGE_SIZE_DEFAULT;
    const num = val ? Number(val) : NaN;
    return Number.isFinite(num) && PAGE_SIZE_OPTIONS.includes(num) ? num : 10;
})();

// Only 2 chips in UI
const STATUS_CHIPS = ["FAILED", "SUCCESS"];

// Stage dropdown options
const STAGE_OPTIONS = ["ALL", "DTVID", "HINGE", "ATTUID"];

const fmtDate = (ts) => {
    if (!ts) return "";
    const s = new Date(ts).toLocaleString();
    const parts = s.split(",");
    if (parts.length < 2) return s;
    const date = (parts[0] || "").trim();
    const time = parts.slice(1).join(",").trim();
    return (
        <span className="opsx-dtwrap">
            <span className="opsx-dt">{date},</span>
            <span className="opsx-tm">{time}</span>
        </span>
    );
};

// Map status -> CSS key (SUCCESS should look like RESOLVED/green)
const statusCssKey = (s) => {
    const v = String(s || "").toUpperCase();
    if (v === "SUCCESS") return "resolved";
    return v.toLowerCase();
};

// Parse NPW input as a single numeric npw_id (or null)
const parseNpwId = (val) => {
    const s = String(val || "").trim();
    if (!s) return null;
    if (!/^\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

function downloadBlob(name, text) {
    const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
}

function toCsv(rows) {
    if (!rows?.length) return "";
    const cols = [
        "ex_id",
        "correlation_id",
        "npw_id",
        "client_request_id",
        "stage",
        "operation",
        "status",
        "http_status",
        "error_code",
        "error_message",
        "retry_count",
        "max_retries",
        "next_retry_at",
        "last_error_at",
        "resolved_at",
        "owner",
        "created_at",
        "updated_at",
    ];
    const esc = (v) =>
        v == null
            ? ""
            : /[",\n]/.test(String(v))
                ? `"${String(v).replace(/"/g, '""')}"`
                : String(v);

    return cols.join(",") + "\n" + rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\n");
}

/* =========================================================
   Retry orchestration
   ========================================================= */

function deriveOrchestratorStage(stage) {
    const s = String(stage || "").trim().toUpperCase();
    if (s === "DTVID") return ["DTVID", "HINGE"];
    if (s === "HINGE") return ["HINGE"];
    if (s === "ATTUID") return ["ATTUID"];
    return null;
}

function deriveReqTypeFromOperation(operation) {
    const op = String(operation || "").toUpperCase();
    if (/(OFFBOARD|OFFBOARDING|TERMINATE|TERMINATION|DISCONNECT|DEPROVISION|REMOVE)/.test(op)) {
        return "offboarding";
    }
    return "onboarding";
}

function deriveEnv() {
    const v =
        ENV.APP_ENV ||
        ENV.ENV ||
        ENV.STAGE ||
        ENV.DEPLOY_ENV ||
        ENV.RUNTIME_ENV ||
        "";
    return String(v || "dev").trim() || "dev";
}

function buildOrchestratorPayloadFromRow(row) {
    const npwId = Number(row?.npw_id);
    if (!Number.isFinite(npwId) || npwId <= 0) return null;

    const stage = deriveOrchestratorStage(row?.stage);
    if (!stage) return null;

    const req_type = deriveReqTypeFromOperation(row?.operation);

    const client_request_id =
        row?.client_request_id ||
        row?.correlation_id ||
        undefined;

    return {
        env: deriveEnv(),
        req_type,
        npw_id: npwId,
        stage,
        ...(client_request_id ? { client_request_id: String(client_request_id) } : {}),
        meta: {
            source: "OpsExceptions",
            ex_id: row?.ex_id ?? null,
            error_code: row?.error_code ?? null,
            operation: row?.operation ?? null,
            stage_from_queue: row?.stage ?? null,
        }
    };
}

function extractErrMsg(e) {
    const msg =
        e?.message ||
        e?.response?.message ||
        e?.response?.data?.message ||
        e?.data?.message ||
        e?.error ||
        e?.data?.error ||
        "";
    return String(msg || "Request failed");
}

function summarizeOrchestratorResponse(resp) {
    if (!resp || typeof resp !== "object") return { ok: true, message: "Retry accepted." };

    const topOk = resp?.summary?.ok ?? resp?.ok ?? null;
    const results = Array.isArray(resp?.results) ? resp.results : [];
    const totalStages = results.length;
    const okStages = results.filter((r) => r && r.ok === true).length;
    const badStages = results.filter((r) => r && r.ok === false).length;

    if (typeof topOk === "boolean") {
        if (topOk) {
            return {
                ok: true,
                message: totalStages ? `Retry succeeded (${okStages}/${totalStages} stages).` : "Retry succeeded.",
            };
        }
        return {
            ok: false,
            message: totalStages ? `Retry failed (${okStages}/${totalStages} stages).` : "Retry failed.",
        };
    }

    if (totalStages) {
        if (badStages === 0) return { ok: true, message: `Retry succeeded (${okStages}/${totalStages} stages).` };
        return { ok: false, message: `Retry failed (${okStages}/${totalStages} stages).` };
    }

    return { ok: true, message: "Retry completed." };
}

export default function OpsExceptions() {
    const { inProgress } = useMsal();
    const [authReady, setAuthReady] = useState(false);
    useEffect(() => {
        if (inProgress === InteractionStatus.None) setAuthReady(true);
    }, [inProgress]);

    const listUrl = ENV.EXCEPTIONS_LIST_URL;
    const retryUrl = ENV.EXCEPTION_RETRY_URL;

    /* data */
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    /* retry loading */
    const [retrying, setRetrying] = useState(false);

    /* paging/filters */
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const [status, setStatus] = useState("FAILED");
    const [stage, setStage] = useState("ALL");
    const [npwLike, setNpwLike] = useState("");
    const [q, setQ] = useState("");

    /* sorting */
    const [sortField, setSortField] = useState("updated_at");
    const [sortOrder, setSortOrder] = useState("desc");

    /* selection + overlay */
    const [selected, setSelected] = useState(new Set());
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        document.body.classList.toggle("opsx-lock", !!detail);
    }, [detail]);

    const rowMap = useMemo(() => {
        const m = new Map();
        rows.forEach((r) => m.set(r.ex_id, r));
        return m;
    }, [rows]);

    const load = async ({ keepSelection = false } = {}) => {
        if (!listUrl) return;
        setLoading(true);
        try {
            const npwId = parseNpwId(npwLike);

            const statusParam = npwId != null ? "BOTH" : String(status || "FAILED");

            const params = new URLSearchParams({
                page: String(page),
                limit: String(pageSize),
                status: statusParam,
                sortField: String(sortField || "updated_at"),
                sortOrder: String(sortOrder || "desc"),
            });

            if (stage && stage !== "ALL") params.set("stage", stage);
            if (npwId != null) params.set("npw_id", String(npwId));
            if (q.trim()) params.set("q", q.trim());

            const data = await tbGetJson(`${listUrl}?${params.toString()}`);
            const nextRows = Array.isArray(data?.rows) ? data.rows : [];
            const nextTotal = Number.isFinite(data?.total) ? data.total : nextRows.length;

            setRows(nextRows);
            setTotal(nextTotal);
            if (!keepSelection) setSelected(new Set());
        } catch (e) {
            console.error("[Exceptions] load error:", e);
            setRows([]);
            setTotal(0);
            if (!keepSelection) setSelected(new Set());
            toast.error(`Load failed: ${extractErrMsg(e)}`, { toastId: "opsx-load-failed" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authReady) queueMicrotask(() => load());
        // eslint-disable-next-line
    }, [authReady]);

    useEffect(() => {
        if (authReady) load();
        // eslint-disable-next-line
    }, [page, pageSize, status, stage, npwLike, q, sortField, sortOrder]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const toggleAll = (checked) =>
        checked ? setSelected(new Set(rows.map((r) => r.ex_id))) : setSelected(new Set());

    const toggleOne = (id, checked) => {
        setSelected((prev) => {
            const cp = new Set(prev);
            checked ? cp.add(id) : cp.delete(id);
            return cp;
        });
    };

    const selectedRows = useMemo(
        () => Array.from(selected).map((id) => rowMap.get(id)).filter(Boolean),
        [selected, rowMap]
    );

    const isSuccessRow = (r) => String(r?.status || "").toUpperCase() === "SUCCESS";

    const onSort = (field) => {
        setPage(1);
        setSelected(new Set());
        setDetail(null);

        if (sortField === field) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortOrder("desc");
        }
    };

    const sortIcon = (field) => {
        if (sortField !== field) return "";
        return sortOrder === "asc" ? " ▲" : " ▼";
    };

    const SortTh = ({ field, className, children, title }) => (
        <th
            className={className}
            title={title || "Click to sort"}
            onClick={() => !retrying && onSort(field)}
            style={{ cursor: retrying ? "not-allowed" : "pointer", userSelect: "none", opacity: retrying ? 0.7 : 1 }}
        >
            {children}
            <span className="opsx-sort">{sortIcon(field)}</span>
        </th>
    );

    /**
     * SINGLE-TOAST PATTERN for retries:
     * - One "submitted" toast (loading) for the whole action
     * - Updated once complete with success/failure summary
     */
    const retryRowsViaOrchestrator = async (rowsToRetry) => {
        const toastId = "opsx-retry-batch";

        if (!retryUrl) {
            toast.error("Retry URL is not configured (ENV.EXCEPTION_RETRY_URL).", { toastId: "opsx-retry-missing-url" });
            return { ok: false };
        }

        if (!rowsToRetry?.length) {
            toast.info("No rows selected for retry.", { toastId: "opsx-retry-none" });
            return { ok: true };
        }

        const payloadsAll = rowsToRetry.map(buildOrchestratorPayloadFromRow);
        const invalid = payloadsAll.filter((p) => !p).length;
        const payloads = payloadsAll.filter(Boolean);

        if (!payloads.length) {
            toast.error("Cannot retry: selected rows are missing NPW ID or have invalid stage.", { toastId: "opsx-retry-invalid" });
            return { ok: false };
        }

        // Show ONE submitted toast as loading
        setRetrying(true);
        setLoading(true);

        toast.loading(`Retry submitted for ${payloads.length} item(s).`, {
            toastId,
            autoClose: false,
            closeOnClick: true,
        });

        let okCount = 0;
        let failCount = 0;

        try {
            // sequential for predictable behavior
            for (const p of payloads) {
                try {
                    const resp = await tbPostJson(retryUrl, p);
                    const summary = summarizeOrchestratorResponse(resp);
                    if (summary.ok) okCount += 1;
                    else failCount += 1;
                } catch (e) {
                    failCount += 1;
                    // still continue
                    console.error("[Exceptions] retry error:", p?.npw_id, e);
                }
            }

            await load({ keepSelection: true });

            const finalOk = failCount === 0;
            const finalMsg =
                finalOk
                    ? `Retry completed: ${okCount}/${payloads.length} succeeded.`
                    : `Retry completed: ${okCount}/${payloads.length} succeeded, ${failCount} failed.`;

            toast.update(toastId, {
                render: finalMsg,
                type: finalOk ? "success" : "warning",
                isLoading: false,
                autoClose: 4000,
            });

            if (invalid) {
                toast.warn(`Skipped ${invalid} row(s) due to missing NPW ID/stage.`, { toastId: "opsx-retry-skipped" });
            }

            return { ok: finalOk, submitted: okCount, failed: failCount };
        } catch (e) {
            toast.update(toastId, {
                render: `Retry failed: ${extractErrMsg(e)}`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });
            return { ok: false };
        } finally {
            setRetrying(false);
            setLoading(false);
        }
    };

    const onRetrySelected = async () => {
        await retryRowsViaOrchestrator(selectedRows);
    };

    const onRetryOne = async (row) => {
        await retryRowsViaOrchestrator([row]);
    };

    return (
        <AppShell activeKey="/ops/exceptions">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="opsx-wrap">
                <div className="opsx-card">
                    {/* Header */}
                    <div className="opsx-bar">
                        <div className="opsx-title">
                            <h2>Exceptions Queue</h2>
                        </div>
                        <div className="opsx-right">
                            <button
                                className="opsx-btn ghost"
                                disabled={!selected.size || !retryUrl || retrying}
                                onClick={onRetrySelected}
                                title={!retryUrl ? "Missing EXCEPTION_RETRY_URL in ENV" : ""}
                            >
                                {retrying ? "Retrying..." : "Retry Selected"}
                            </button>
                            <button
                                className="opsx-btn"
                                onClick={() => downloadBlob(`exceptions_${Date.now()}.csv`, toCsv(rows))}
                                disabled={retrying}
                            >
                                Export CSV
                            </button>
                        </div>
                    </div>

                    {/* Filters row */}
                    <div className="opsx-toolbar">
                        <div className="opsx-toolbar-row">
                            <div className="opsx-chipbar">
                                {STATUS_CHIPS.map((s) => {
                                    const chipCssKey = s === "SUCCESS" ? "resolved" : s.toLowerCase();
                                    return (
                                        <button
                                            key={s}
                                            className={`opsx-chip st-${chipCssKey} ${status === s ? "active" : ""}`}
                                            disabled={retrying}
                                            onClick={() => {
                                                setStatus(s);
                                                setPage(1);
                                                setSelected(new Set());
                                                setDetail(null);
                                            }}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="opsx-inputs-inline">
                                <select
                                    className="opsx-input"
                                    aria-label="Stage"
                                    value={stage}
                                    disabled={retrying}
                                    onChange={(e) => {
                                        setStage(e.target.value);
                                        setPage(1);
                                        setSelected(new Set());
                                        setDetail(null);
                                    }}
                                    style={{ maxWidth: 140 }}
                                >
                                    {STAGE_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>

                                <input
                                    className="opsx-input"
                                    placeholder="NPW ID (numeric)"
                                    aria-label="NPW"
                                    value={npwLike}
                                    disabled={retrying}
                                    onChange={(e) => {
                                        setNpwLike(e.target.value);
                                        setPage(1);
                                        setSelected(new Set());
                                        setDetail(null);
                                    }}
                                />
                                <input
                                    className="opsx-input"
                                    placeholder="Search message/code/NF"
                                    aria-label="Search"
                                    value={q}
                                    disabled={retrying}
                                    onChange={(e) => {
                                        setQ(e.target.value);
                                        setPage(1);
                                        setSelected(new Set());
                                        setDetail(null);
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="opsx-table-wrap">
                        <table className="opsx-table">
                            <thead>
                                <tr>
                                    <th className="opsx-col-select" style={{ width: 28 }}>
                                        <input
                                            type="checkbox"
                                            checked={rows.length > 0 && selected.size === rows.length}
                                            onChange={(e) => toggleAll(e.target.checked)}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={rows.length === 0 || retrying}
                                        />
                                    </th>

                                    <SortTh field="npw_id" className="opsx-col-npw">NPW</SortTh>
                                    <SortTh field="stage" className="opsx-col-stage">Stage</SortTh>
                                    <SortTh field="status" className="opsx-col-status">Status</SortTh>

                                    <th className="opsx-col-op">Op</th>
                                    <th className="opsx-col-error">Error</th>

                                    {/*
                                      NOTE: Temporarily commented (do not delete) so the table fits without horizontal scroll.
                                      Uncomment when we want these columns back in the grid view.
                                    */}
                                    {/* <SortTh field="retry_count" className="opsx-col-retries">Retries</SortTh> */}
                                    {/* <SortTh field="next_retry_at" className="opsx-col-next">Next</SortTh> */}
                                    <SortTh field="updated_at" className="opsx-col-updated">Updated</SortTh>

                                    <th className="opsx-col-actions">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.ex_id} className={`opsx-row-${statusCssKey(r.status)}`}>
                                        <td className="opsx-col-select">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(r.ex_id)}
                                                onChange={(e) => toggleOne(r.ex_id, e.target.checked)}
                                                onClick={(e) => e.stopPropagation()}
                                                disabled={retrying}
                                            />
                                        </td>

                                        <td className="opsx-col-npw">{r.npw_id || ""}</td>

                                        <td className="opsx-col-stage">
                                            <span className="opsx-pill">{r.stage}</span>
                                        </td>

                                        <td className="opsx-col-status">
                                            <span className={`opsx-badge st-${statusCssKey(r.status)}`}>{r.status}</span>
                                        </td>

                                        <td className="opsx-col-op opsx-fade">{r.operation || ""}</td>

                                        <td className="opsx-col-error opsx-ellipsis" title={r.error_message || ""}>
                                            {r.error_message || ""}
                                        </td>

                                        {/* <td className="opsx-col-retries opsx-mono">{r.retry_count ?? 0}/{r.max_retries ?? 0}</td> */}

                                        {/* <td className="opsx-col-next opsx-fade">{fmtDate(r.next_retry_at)}</td> */}

                                        <td className="opsx-col-updated opsx-fade">{fmtDate(r.updated_at)}</td>

                                        <td className="opsx-col-actions">
                                            <div className="opsx-row-actions" onClick={(e) => e.stopPropagation()}>
                                                <button className="opsx-link" onClick={() => setDetail(r)} disabled={retrying}>View</button>
                                                <span className="opsx-sep">•</span>
                                                <button
                                                    className="opsx-link"
                                                    disabled={isSuccessRow(r) || !retryUrl || retrying}
                                                    title={
                                                        isSuccessRow(r)
                                                            ? "Retry is disabled for SUCCESS records"
                                                            : (!retryUrl ? "Missing EXCEPTION_RETRY_URL in ENV" : "")
                                                    }
                                                    onClick={() => onRetryOne(r)}
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                                {!rows.length && !loading && (
                                    <tr>
                                        <td colSpan={8} className="opsx-fade" style={{ padding: 16 }}>
                                            No records found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer / Pager */}
                    <div className="opsx-table-foot">
                        <div className="opsx-left">
                            <div className="opsx-muted">
                                Showing <strong>{rows.length}</strong> of <strong>{total}</strong>
                                {loading ? " • Loading…" : ""}
                                {retrying ? " • Retrying…" : ""}
                            </div>
                        </div>

                        <div className="opsx-right opsx-pager-controls">
                            <span className="opsx-pager-label">Rows</span>
                            <select
                                value={pageSize}
                                disabled={retrying}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>

                            <button className="opsx-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || retrying}>
                                Prev
                            </button>

                            <span>Page {page} / {totalPages}</span>

                            <button className="opsx-btn" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages || retrying}>
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Overlay */}
            {detail && (
                <OpsExceptionOverlay
                    row={detail}
                    onClose={() => setDetail(null)}
                    onRetry={async (row) => {
                        // Keep overlay open; this page handles the single-toast pattern.
                        await onRetryOne(row || detail);

                        // Refresh overlay row if it still exists after reload
                        const refreshed = rowMap.get(detail.ex_id);
                        if (refreshed) setDetail(refreshed);
                    }}
                />
            )}
        </AppShell>
    );
}
